/**
 * Закрытие раскрытого списка щелчком мимо него.
 *
 * Так ведёт себя обычный `<select>` браузера, и от списка рядом с ним ждут
 * того же: выбор конструкции сворачивается по щелчку в пустом месте, а список
 * доп. элементов и цветов оставался открытым — приходилось искать стрелку.
 *
 * Это НЕ то же самое, что закрытие окна по щелчку вне: там щелчок мимо решал
 * судьбу несохранённых правок, и его убрали намеренно. Здесь закрывается лишь
 * раскрытая часть поля — выбранное остаётся на месте, терять нечего.
 *
 * Слушается `mousedown`, а не `click`: щелчок по пункту списка успевает
 * отработать раньше, чем всплывёт `click`, и выбор не теряется.
 */

import { useEffect, useRef } from 'react';

export function useOutsideClose<T extends HTMLElement>(active: boolean, onClose: () => void) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!active) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && ref.current?.contains(target)) return;
      onClose();
    };

    // Escape закрывает тем же движением, что и в остальных списках программы.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [active, onClose]);

  return ref;
}
